import { useEffect, useRef, useState } from 'react';
import apiClient from '../services/apiClient';
export const SCENE_AUTO_ADVANCE_DELAY = 5000;
export const sceneCaptionDelay = () => SCENE_AUTO_ADVANCE_DELAY;
const KEY = 'scene_voice_muted';

export default function useScenePlayback(scene, frameIndex, advance, fetchAudio = apiClient.getSceneFrameAudio, isPaused = false) {
  const [muted, setMuted] = useState(() => { try { return localStorage.getItem(KEY) === 'true'; } catch { return false; } });
  const [blocked, setBlocked] = useState(false);
  const [autoAdvance, setAutoAdvance] = useState(() => { try { return localStorage.getItem('scene_auto_advance') !== 'false'; } catch { return true; } });
  const autoEnabled = useRef(autoAdvance), updateTimer = useRef(() => {});
  autoEnabled.current = autoAdvance;
  useEffect(() => { updateTimer.current(); }, [autoAdvance]);
  const [visibility, setVisibility] = useState(0);
  const element = useRef(null), nextAction = useRef(advance), retry = useRef(() => {}), stop = useRef(() => {});
  const cache = useRef(new Map());
  nextAction.current = advance;
  useEffect(() => {
    const changed = () => setVisibility(value => value + 1);
    document.addEventListener('visibilitychange', changed);
    return () => document.removeEventListener('visibilitychange', changed);
  }, []);
  useEffect(() => {
    const entries = cache.current;
    return () => {
      for (const value of entries.values()) { value.controller.abort(); value.promise.then(url => URL.revokeObjectURL(url)).catch(() => {}); }
      entries.clear();
    };
  }, [scene.id]);
  useEffect(() => {
    let cancelled = false, timer, timeout, waiting = false;
    const frame = scene.frames[frameIndex];
    let audio, currentKey;
    const clear = () => { clearTimeout(timer); clearTimeout(timeout); };
    const cleanup = () => {
      cancelled = true; clear();
      const pending = cache.current.get(currentKey);
      if (pending && !pending.loaded) { pending.controller.abort(); cache.current.delete(currentKey); }
      if (audio) { audio.onended = null; audio.onerror = null; audio.onstalled = null; audio.onplaying = null; audio.pause(); audio.removeAttribute('src'); audio.load(); }
    };
    stop.current = cleanup;
    setBlocked(false);
    if (!frame || document.hidden || isPaused) return cleanup;
    const schedule = delay => {
      clear(); waiting = true;
      if (autoEnabled.current) timer = setTimeout(() => { if (!cancelled && autoEnabled.current) nextAction.current(); }, delay);
    };
    updateTimer.current = () => { if (!cancelled && waiting) schedule(SCENE_AUTO_ADVANCE_DELAY); };
    const fallback = () => { if (!cancelled) { audio?.pause(); schedule(sceneCaptionDelay(frame.text)); } };
    const hasAudio = item => item && !item.audio?.silent && (item.audioUrl || (scene.playbackId && (item.audio?.dynamic || item.audio?.onDemand)));
    if (muted || !hasAudio(frame)) { fallback(); return cleanup; }
    const prepare = item => {
      const key = `${scene.id}:${item.id || frameIndex}:${item.audio?.key || item.audioUrl}`;
      if (!cache.current.has(key)) {
        const controller = new AbortController();
        const entry = { controller, loaded: false };
        entry.promise = fetchAudio({ scene, frame: item, signal: controller.signal }).then(blob => {
          entry.loaded = true;
          const url = URL.createObjectURL(blob);
          if (cache.current.get(key) !== entry) URL.revokeObjectURL(url);
          return url;
        });
        cache.current.set(key, entry);
        entry.promise.catch(() => { if (cache.current.get(key) === entry) cache.current.delete(key); });
      }
      return cache.current.get(key).promise;
    };
    if (!element.current) element.current = new Audio();
    audio = element.current;
    const play = () => {
      if (cancelled || document.hidden) return;
      waiting = false;
      clear();
      timeout = setTimeout(fallback, 12000);
      try {
        Promise.resolve(audio.play()).then(() => {
          if (cancelled) return;
          clear();
          setBlocked(false);
          const next = scene.frames[frameIndex + 1];
          if (hasAudio(next)) prepare(next).catch(() => {});
        }).catch(error => {
          if (cancelled) return;
          setBlocked(error?.name === 'NotAllowedError'); fallback();
        });
      } catch { fallback(); }
    };
    audio.onended = () => { if (!cancelled) schedule(SCENE_AUTO_ADVANCE_DELAY); };
    audio.onerror = fallback;
    audio.onstalled = () => { clearTimeout(timeout); timeout = setTimeout(fallback, 12000); };
    audio.onplaying = () => { clearTimeout(timeout); };
    retry.current = play;
    timeout = setTimeout(fallback, 12000);
    currentKey = `${scene.id}:${frame.id || frameIndex}:${frame.audio?.key || frame.audioUrl}`;
    prepare(frame).then(url => { if (!cancelled) { clear(); audio.src = url; play(); } }).catch(fallback);
    return cleanup;
  }, [scene, frameIndex, muted, visibility, fetchAudio, isPaused]);
  const toggleMuted = () => {
    stop.current();
    setMuted(value => { try { localStorage.setItem(KEY, String(!value)); } catch {} return !value; });
  };
  const toggleAutoAdvance = () => setAutoAdvance(value => {
    try { localStorage.setItem('scene_auto_advance', String(!value)); } catch {}
    return !value;
  });
  return { muted, blocked, autoAdvance, toggleAutoAdvance, toggleMuted, retry: () => retry.current(), stop: () => stop.current() };
}
